import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import JsBarcode from "jsbarcode";
import {
  BsArrowLeft,
  BsBoxSeam,
  BsCloudUpload,
  BsUpcScan,
} from "react-icons/bs";

import Sidebar from "../../components/sidebar/Sidebar";
import ActivityLog from "../activity/ActivityLog";

import {
  serviceGetProductDetails,
  serviceUpdateProduct,
} from "../../api/serviceApi";

import {
  serviceDeletePhoto,
  serviceUploadPhoto,
} from "../../api/servicePhotoApi";

import "./inventoryproductupdate.css";
import { getImageUrl } from "../../utils/helper";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const PRODUCT_TYPES = [
  "Air Fittings",
  "aluminium block",
  "Brackets",
  "Computer",
  "Display",
  "Documentation",
  "Electrical Cable",
  "Electrical Connectors",
  "Electronics",
  "Fasteners",
  "Other Fittings",
  "PCB",
  "Sensors",
  "Tools",
];

const InventoryProductUpdate = () => {
  const navigate = useNavigate();
  const { productId } = useParams();

  const barcodeRef = useRef(null);
  const fileInputRef = useRef(null);

  const [state, setState] = useState(null);
  const [originalProduct, setOriginalProduct] = useState(null);

  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [imageError, setImageError] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ==========================================================
  // LOAD PRODUCT
  // ==========================================================

  useEffect(() => {
    let cancelled = false;

    const loadProduct = async () => {
      try {
        setLoading(true);
        setLoadError(false);

        const product = await serviceGetProductDetails(productId);

        if (cancelled) {
          return;
        }

        setState(product);
        setOriginalProduct(product);
      } catch (error) {
        console.error("Error loading product:", error);

        if (!cancelled) {
          setState(null);
          setOriginalProduct(null);
          setLoadError(true);

          toast.error(error?.message || "Failed to load product.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [productId]);

  // ==========================================================
  // IMAGE PREVIEW
  // ==========================================================

  useEffect(() => {
    if (!image) {
      setPreviewUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(image);

    setPreviewUrl(objectUrl);
    setImageError(false);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [image]);

  // ==========================================================
  // BARCODE
  // ==========================================================

  useEffect(() => {
    if (!state?.mpn || !barcodeRef.current) {
      return;
    }

    try {
      JsBarcode(barcodeRef.current, state.mpn, {
        format: "CODE128",
        lineColor: "#000000",
        width: 2,
        height: 52,
        displayValue: true,
        fontSize: 13,
        margin: 8,
      });
    } catch (error) {
      console.error("Barcode render error:", error);
    }
  }, [state?.mpn]);

  // ==========================================================
  // GST
  // ==========================================================

  const gst = useMemo(() => {
    const cost = Number(state?.cost);

    if (!Number.isFinite(cost) || cost <= 0) {
      return 0;
    }

    return Number((cost * 0.1).toFixed(2));
  }, [state?.cost]);

  const costIncGst = useMemo(() => {
    const cost = Number(state?.cost);

    if (!Number.isFinite(cost) || cost <= 0) {
      return 0;
    }

    return Number((cost + gst).toFixed(2));
  }, [state?.cost, gst]);

  // ==========================================================
  // INPUT
  // ==========================================================

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setState((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  // ==========================================================
  // IMAGE
  // ==========================================================

  const handleImageChoose = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error("Image size must be 5MB or less.");
      event.target.value = "";
      return;
    }

    setImage(file);
  };

  const cancelNewImage = () => {
    setImage(null);
    setImageError(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadNewImage = async () => {
    if (!image) {
      return "";
    }

    const safeMpn = String(state?.mpn || productId)
      .trim()
      .replace(/[^A-Za-z0-9_-]/g, "_");

    const filename = `${safeMpn}-${Date.now()}`;

    const result = await serviceUploadPhoto({
      file: image,
      folder: "tims/inventory",
      filename,
    });

    if (!result?.url) {
      throw new Error("Image upload failed.");
    }

    return result.url;
  };

  // ==========================================================
  // VALIDATION
  // ==========================================================

  const validateForm = () => {
    const name = state?.name?.trim();
    const cost = Number(state?.cost);

    if (!name) {
      toast.error("Product name required.");
      return false;
    }

    if (!Number.isFinite(cost) || cost < 0) {
      toast.error("Please enter a valid product cost.");
      return false;
    }

    const quantity = Number(state?.quantity);
    const minQuantity = Number(state?.minQuantity);
    const maxQuantity = Number(state?.maxQuantity);

    if (quantity < 0 || minQuantity < 0 || maxQuantity < 0) {
      toast.error("Stock quantities cannot be negative.");
      return false;
    }

    if (maxQuantity > 0 && minQuantity > maxQuantity) {
      toast.error("Minimum quantity cannot be greater than maximum quantity.");
      return false;
    }

    return true;
  };

  // ==========================================================
  // BUILD UPDATE PAYLOAD
  // ==========================================================

  const buildUpdatePayload = (newImageUrl) => {
    const current = {
      name: state?.name?.trim() || "",
      shelfAddress: state?.shelfAddress?.trim() || "",
      description: state?.description?.trim() || "",
      type: state?.type || "",
      supplier: state?.supplier?.trim() || "",

      cost: Number(state?.cost) || 0,
      wholesale: Number(state?.wholesale) || 0,
      retail: Number(state?.retail) || 0,

      quantity: Number(state?.quantity) || 0,
      minQuantity: Number(state?.minQuantity) || 0,
      maxQuantity: Number(state?.maxQuantity) || 0,
    };

    const original = {
      name: originalProduct?.name?.trim() || "",
      shelfAddress: originalProduct?.shelfAddress?.trim() || "",
      description: originalProduct?.description?.trim() || "",
      type: originalProduct?.type || "",
      supplier: originalProduct?.supplier?.trim() || "",

      cost: Number(originalProduct?.cost) || 0,
      wholesale: Number(originalProduct?.wholesale) || 0,
      retail: Number(originalProduct?.retail) || 0,

      quantity: Number(originalProduct?.quantity) || 0,
      minQuantity: Number(originalProduct?.minQuantity) || 0,
      maxQuantity: Number(originalProduct?.maxQuantity) || 0,
    };

    const payload = {
      id: productId,
    };

    Object.entries(current).forEach(([key, value]) => {
      if (value !== original[key]) {
        payload[key] = value;
      }
    });

    if (newImageUrl) {
      payload.imageUrl = newImageUrl;
    }

    return payload;
  };

  // ==========================================================
  // SUBMIT
  // ==========================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    let newImageUrl = "";

    try {
      setSubmitting(true);

      if (image) {
        newImageUrl = await uploadNewImage();
      }

      const updateData = buildUpdatePayload(newImageUrl);

      const changedFields = Object.keys(updateData).filter(
        (key) => key !== "id",
      );

      if (changedFields.length === 0) {
        toast.info("No changes to update.");
        return;
      }

      await serviceUpdateProduct(updateData);

      if (
        newImageUrl &&
        originalProduct?.imageUrl &&
        originalProduct.imageUrl !== newImageUrl
      ) {
        try {
          await serviceDeletePhoto(originalProduct.imageUrl);
        } catch (error) {
          console.error("Product updated but old image cleanup failed:", error);
        }
      }

      ActivityLog(
        "Product Updated",
        `${originalProduct?.mpn || productId} MPN updated`,
      );

      toast.success("Product updated successfully.");

      navigate(`/inventory/view/${productId}`);
    } catch (error) {
      console.error("Error updating product:", error);

      if (newImageUrl) {
        try {
          await serviceDeletePhoto(newImageUrl);
        } catch (deleteError) {
          console.error("Failed to clean up new image:", deleteError);
        }
      }

      toast.error(error?.message || "Failed to update product.");
    } finally {
      setSubmitting(false);
    }
  };

  const existingImageUrl = getImageUrl(state?.imageUrl);

  const displayedImageUrl = previewUrl || existingImageUrl;

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div className="update-product-page">
        <Sidebar />

        <main className="update-product-content">
          <div className="update-product-container">
            <div className="update-product-state">
              <div className="update-product-state-icon">
                <BsBoxSeam />
              </div>

              <strong>Loading product...</strong>

              <span>Please wait while the product details are loaded.</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================================
  // ERROR
  // ==========================================================

  if (loadError || !state) {
    return (
      <div className="update-product-page">
        <Sidebar />

        <main className="update-product-content">
          <div className="update-product-container">
            <div className="update-product-state">
              <div className="update-product-state-icon">
                <BsBoxSeam />
              </div>

              <strong>Product not found</strong>

              <span>
                The product could not be loaded or may no longer exist.
              </span>

              <button
                type="button"
                className="update-product-btn primary"
                onClick={() => navigate("/inventory")}
              >
                <BsArrowLeft />
                Back to Inventory
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="update-product-page">
      <Sidebar />

      <main className="update-product-content">
        <div className="update-product-container">
          <header className="update-product-header">
            <button
              type="button"
              className="update-product-back"
              onClick={() => navigate(`/inventory/view/${productId}`)}
              disabled={submitting}
            >
              <BsArrowLeft />
              Back to Product
            </button>

            <h1>Update Product</h1>

            <p>Update product information, pricing and inventory settings.</p>
          </header>

          <form className="update-product-card" onSubmit={handleSubmit}>
            {/* ================================================
                SIDEBAR
            ================================================= */}

            <aside className="update-product-sidebar">
              <div className="update-product-image-preview">
                {displayedImageUrl && !imageError ? (
                  <img
                    src={displayedImageUrl}
                    alt={state.name || "Product preview"}
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <BsBoxSeam />
                )}
              </div>

              <div className="update-product-image-info">
                <strong>Product Image</strong>

                <span>Upload a replacement image for this product.</span>
              </div>

              <label
                htmlFor="update-product-image"
                className="update-product-upload"
              >
                <BsCloudUpload />

                {image ? "Change Image" : "Replace Image"}
              </label>

              <input
                ref={fileInputRef}
                id="update-product-image"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif"
                className="update-product-file"
                onChange={handleImageChoose}
                disabled={submitting}
              />

              {image && (
                <div className="update-product-selected-file">
                  <span>{image.name}</span>

                  <button
                    type="button"
                    onClick={cancelNewImage}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              )}

              {submitting && image && (
                <div className="update-product-progress">
                  <span>Uploading replacement image...</span>
                </div>
              )}

              {state.mpn && (
                <div className="update-product-barcode">
                  <div className="update-product-barcode-title">
                    <BsUpcScan />
                    <strong>Product Barcode</strong>
                  </div>

                  <canvas ref={barcodeRef} />
                </div>
              )}
            </aside>

            {/* ================================================
                FORM
            ================================================= */}

            <div className="update-product-form">
              <section className="update-product-section">
                <div className="update-product-section-header">
                  <h2>Product Details</h2>

                  <p>Basic product and inventory identification.</p>
                </div>

                <div className="update-product-grid">
                  <div className="update-product-field">
                    <label htmlFor="name">
                      Name <span>*</span>
                    </label>

                    <input
                      id="name"
                      type="text"
                      name="name"
                      value={state.name || ""}
                      placeholder="Product name"
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                  </div>

                  <div className="update-product-field">
                    <label htmlFor="type">Type</label>

                    <select
                      id="type"
                      name="type"
                      value={state.type || ""}
                      onChange={handleInputChange}
                      disabled={submitting}
                    >
                      <option value="">Select product type...</option>

                      {PRODUCT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="update-product-field update-product-full">
                    <label htmlFor="description">Description</label>

                    <textarea
                      id="description"
                      name="description"
                      rows="3"
                      value={state.description || ""}
                      placeholder="Product description"
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                  </div>

                  <div className="update-product-field">
                    <label htmlFor="shelfAddress">Shelf Address</label>

                    <input
                      id="shelfAddress"
                      type="text"
                      name="shelfAddress"
                      value={state.shelfAddress || ""}
                      placeholder="e.g. A-12"
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                  </div>

                  <div className="update-product-field">
                    <label htmlFor="mpn">MPN</label>

                    <input
                      id="mpn"
                      type="text"
                      value={state.mpn || ""}
                      disabled
                    />
                  </div>

                  <div className="update-product-field">
                    <label htmlFor="supplier">Supplier</label>

                    <input
                      id="supplier"
                      type="text"
                      name="supplier"
                      value={state.supplier || ""}
                      placeholder="Supplier"
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                  </div>
                </div>
              </section>

              <div className="update-product-divider" />

              <section className="update-product-section">
                <div className="update-product-section-header">
                  <h2>Pricing</h2>

                  <p>Product cost, GST and selling prices.</p>
                </div>

                <div className="update-product-grid three-col">
                  <div className="update-product-field">
                    <label htmlFor="cost">
                      Cost Ex GST <span>*</span>
                    </label>

                    <input
                      id="cost"
                      type="number"
                      name="cost"
                      min="0"
                      step="0.01"
                      value={state.cost ?? ""}
                      placeholder="0.00"
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                  </div>

                  <div className="update-product-field">
                    <label htmlFor="gst">GST</label>

                    <input id="gst" type="number" value={gst} disabled />
                  </div>

                  <div className="update-product-field">
                    <label htmlFor="costIncGst">Cost Inc GST</label>

                    <input
                      id="costIncGst"
                      type="number"
                      value={costIncGst}
                      disabled
                    />
                  </div>

                  <div className="update-product-field">
                    <label htmlFor="wholesale">Wholesale</label>

                    <input
                      id="wholesale"
                      type="number"
                      name="wholesale"
                      min="0"
                      step="0.01"
                      value={state.wholesale ?? ""}
                      placeholder="0.00"
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                  </div>

                  <div className="update-product-field">
                    <label htmlFor="retail">Retail</label>

                    <input
                      id="retail"
                      type="number"
                      name="retail"
                      min="0"
                      step="0.01"
                      value={state.retail ?? ""}
                      placeholder="0.00"
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                  </div>
                </div>
              </section>

              <div className="update-product-divider" />

              <section className="update-product-section">
                <div className="update-product-section-header">
                  <h2>Stock Levels</h2>

                  <p>Current quantity and stock alert thresholds.</p>
                </div>

                <div className="update-product-grid three-col">
                  <div className="update-product-field">
                    <label htmlFor="quantity">Quantity</label>

                    <input
                      id="quantity"
                      type="number"
                      name="quantity"
                      min="0"
                      step="1"
                      value={state.quantity ?? ""}
                      placeholder="0"
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                  </div>

                  <div className="update-product-field">
                    <label htmlFor="minQuantity">Minimum Quantity</label>

                    <input
                      id="minQuantity"
                      type="number"
                      name="minQuantity"
                      min="0"
                      step="1"
                      value={state.minQuantity ?? ""}
                      placeholder="0"
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                  </div>

                  <div className="update-product-field">
                    <label htmlFor="maxQuantity">Maximum Quantity</label>

                    <input
                      id="maxQuantity"
                      type="number"
                      name="maxQuantity"
                      min="0"
                      step="1"
                      value={state.maxQuantity ?? ""}
                      placeholder="0"
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                  </div>
                </div>
              </section>

              <div className="update-product-actions">
                <button
                  type="button"
                  className="update-product-btn ghost"
                  onClick={() => navigate(`/inventory/view/${productId}`)}
                  disabled={submitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="update-product-btn primary"
                  disabled={submitting}
                >
                  {submitting ? "Updating Product..." : "Update Product"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default InventoryProductUpdate;
